import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  PaymentFooter,
  type PaymentPolicyHelpPanelContext,
} from "components/PaymentButton";
import UsdtDepositForm, { useUsdtDeposit } from "components/UsdtDepositForm";
import { useCurrencySymbol } from "hooks/useCurrencySymbol";
import { useTranslation } from "react-i18next";
import type { DepositDto } from "types/api/deposits";
import formatter from "utils/formatter";
import { toastResponseError } from "utils/responseMessageHelper";
import { creditedNet, type DepositFee } from "utils/usdtDeposits";
import {
  clearUsdtDepositSession,
  getUsdtDepositSession,
  saveUsdtDepositSession,
  type UsdtDepositSession,
} from "utils/usdtDepositSession";

interface UsdtDepositCheckoutProps {
  // Shortfall the balance must cover before the purchase can be confirmed.
  targetAmount: number;
  // `purchase:{id}` or `purchase_all` — the pay panels are remounted when they
  // return from the policy Help panel, so the session lives in storage.
  sessionKey: string;
  isOpen: boolean;
  helpPanelContext?: PaymentPolicyHelpPanelContext;
  // Pays from the (by then credited) balance: POST …/confirmations.
  confirmPurchase: () => Promise<unknown>;
  // Reloads the profile so the balance the panels show is current.
  refreshProfile: () => Promise<unknown>;
  // Host bookkeeping after a successful payment (list refresh, close).
  onPaid: () => void | Promise<void>;
}

type Phase = "waiting" | "paying" | "failed";

// client.js rejects plain { data, message, status } objects.
const isPaymentRequired = (error: unknown) =>
  (error as { status?: unknown } | null)?.status === 402;

const newestId = (deposits: DepositDto[]) =>
  deposits.reduce((max, deposit) => Math.max(max, deposit.id), 0);

// What the deposits credited since the session started add up to.
const progressFor = (
  deposits: DepositDto[] | null,
  session: UsdtDepositSession | null,
  fee: DepositFee | null,
) => {
  const counted =
    session && deposits
      ? deposits.filter((item) => item.id > session.sinceDepositId)
      : [];
  const credited = fee ? creditedNet(counted, fee) : 0;
  const remaining = session
    ? formatter.roundTo2Decimal(Math.max(session.targetAmount - credited, 0))
    : null;
  return {
    counted,
    credited,
    remaining,
    covered: counted.length > 0 && remaining === 0,
  };
};

// Pays a purchase with transfers to the user's permanent USDT address: the
// deposits credited after the user chose USDT count toward the shortfall, and
// once they cover it the purchase is confirmed from the balance.
const UsdtDepositCheckout: FC<UsdtDepositCheckoutProps> = React.memo(
  ({
    targetAmount,
    sessionKey,
    isOpen,
    helpPanelContext,
    confirmPurchase,
    refreshProfile,
    onPaid,
  }) => {
    const { t } = useTranslation("common");
    const currencySymbol = useCurrencySymbol();
    const [session, setSession] = useState<UsdtDepositSession | null>(() =>
      getUsdtDepositSession(sessionKey),
    );
    const [phase, setPhase] = useState<Phase>("waiting");
    const payingRef = useRef(false);

    // Reads the latest render's session and fee from inside the hook's poll.
    const coversRef = useRef<(all: DepositDto[]) => boolean>(() => false);

    // Partial transfers raise the balance the panels show. A covering one is
    // refreshed only after confirmation (see pay).
    const handleNewDeposits = useCallback(
      (_fresh: DepositDto[], all: DepositDto[]) => {
        if (coversRef.current(all)) return;
        refreshProfile().catch(() => undefined);
      },
      [refreshProfile],
    );

    const deposit = useUsdtDeposit({
      isOpen,
      onNewDeposits: handleNewDeposits,
    });
    const { deposits, fee } = deposit;

    // The session starts once the list is known: only later deposits count.
    useEffect(() => {
      if (session !== null || deposits === null) return;
      const next = { sinceDepositId: newestId(deposits), targetAmount };
      saveUsdtDepositSession(sessionKey, next);
      setSession(next);
    }, [deposits, session, sessionKey, targetAmount]);

    const { counted, credited, remaining, covered } = progressFor(
      deposits,
      session,
      fee,
    );
    coversRef.current = (all) => progressFor(all, session, fee).covered;

    // Confirmation first — the server balance is authoritative, and
    // refreshing the profile earlier would flip Pay All's "needs external
    // payment" and unmount this form mid-flight. The session is kept until
    // confirmation succeeds, so a remount retries instead of asking for more.
    const pay = useCallback(async () => {
      if (payingRef.current) return;
      payingRef.current = true;
      setPhase("paying");
      try {
        await confirmPurchase();
      } catch (error) {
        payingRef.current = false;
        if (isPaymentRequired(error)) {
          // 402: the estimate changed while the user was paying and the
          // balance still falls short. Start over from the current shortfall;
          // the body carries no message of its own.
          toastResponseError(t("usdtDeposit.stillShort"));
          clearUsdtDepositSession(sessionKey);
          setSession(null);
          setPhase("waiting");
        } else {
          toastResponseError(error);
          setPhase("failed");
        }
        refreshProfile().catch(() => undefined);
        return;
      }
      clearUsdtDepositSession(sessionKey);
      await refreshProfile().catch(() => undefined);
      await onPaid();
    }, [confirmPurchase, onPaid, refreshProfile, sessionKey, t]);

    useEffect(() => {
      if (covered && phase === "waiting") void pay();
    }, [covered, pay, phase]);

    let status: ReactNode = null;
    let footer: ReactNode;
    switch (phase) {
      case "paying":
        status = t("usdtDeposit.completing");
        footer = (
          <PaymentFooter isLoading disabled helpPanelContext={helpPanelContext}>
            {t("usdtDeposit.completing")}
          </PaymentFooter>
        );
        break;
      case "failed":
        status = t("usdtDeposit.payFailed");
        footer = (
          <PaymentFooter onClick={pay} helpPanelContext={helpPanelContext}>
            {t("usdtDeposit.retry")}
          </PaymentFooter>
        );
        break;
      default:
        if (counted.length > 0) {
          status = t("usdtDeposit.received", {
            amount: `${currencySymbol}${formatter.currency(credited)}`,
          });
        }
    }

    return (
      <UsdtDepositForm
        deposit={deposit}
        amountDue={covered ? undefined : remaining ?? targetAmount}
        status={status}
        footer={footer}
        helpPanelContext={helpPanelContext}
      />
    );
  },
);

export default UsdtDepositCheckout;
